import { api, type SafetyOverview, type SafetyOccurrence } from '../api';
import { callout, field, input, opt, openDialog, select, val } from '../dialog';
import { errorCard, esc, fmtDate, html, loading, pill, tile } from '../ui';

/**
 * Safety & compliance.
 *
 * The management system, and the one obligation here with a hard external
 * deadline: Reg. (EU) 376/2014 gives 72 hours from becoming aware of an
 * occurrence to reporting it. So the page leads with hours remaining rather
 * than a status word — a number counts down, a label does not.
 */

const CLASSIFICATION: Record<string, string> = {
  ACCIDENT: 'Accident',
  SERIOUS_INCIDENT: 'Serious incident',
  INCIDENT: 'Incident',
  OTHER: 'Other occurrence',
};

const LEVEL_LABEL: Record<string, string> = {
  LEVEL_1: 'Level 1',
  LEVEL_2: 'Level 2',
  OBSERVATION: 'Observation',
};

function clock(o: SafetyOccurrence): string {
  if (o.reportedAt) return `reported ${fmtDate(o.reportedAt)}`;
  if (o.hoursLeft === null) return 'not reportable';
  if (o.hoursLeft < 0) return `${Math.abs(o.hoursLeft).toFixed(0)} h overdue`;
  return `${o.hoursLeft.toFixed(0)} h left`;
}

function view(o: SafetyOverview): string {
  const due = o.occurrences.filter((x) => x.isReportable && !x.reportedAt);

  return html`
    <div class="page-h">
      <div>
        <h1>Safety &amp; compliance</h1>
        <p class="dim">
          Management system under Part-CAMO / Part-CAO · occurrence reporting under Reg. (EU) 376/2014
        </p>
      </div>
      <div style="display:flex;gap:8px">
        <button class="btn" data-finding>Raise finding</button>
        <button class="btn pri" data-report>Report occurrence</button>
      </div>
    </div>

    ${o.summary.reportOverdue
      ? callout('exceed', '✕', html`
          ${o.summary.reportOverdue} occurrence(s) are past the ${o.reportWindowHours}-hour reporting window.
          The clock runs from when the occurrence was <b>detected</b>, not when it happened.
        `)
      : ''}

    <div class="tiles">
      ${tile({
        label: `Reports due ≤ ${o.reportWindowHours} h`,
        value: o.summary.reportDue,
        sub: due[0] ? `${due[0].reference} · ${clock(due[0])}` : 'none outstanding',
        tone: o.summary.reportOverdue ? 'exceed' : o.summary.reportDue ? 'caution' : 'ok',
      })}
      ${tile({
        label: 'Occurrences this year',
        value: o.summary.occurrencesYtd,
        sub: `${o.summary.closedYtd} closed`,
      })}
      ${tile({
        label: 'Open findings',
        value: o.summary.openFindings,
        sub: o.summary.level1 ? `${o.summary.level1} at level 1` : 'none at level 1',
        tone: o.summary.level1 ? 'exceed' : o.summary.openFindings ? 'caution' : 'ok',
      })}
      ${tile({
        label: 'Audit plan',
        value: `${o.summary.auditsDone}/${o.summary.auditsPlanned}`,
        sub: 'audits completed',
        tone: o.summary.auditsDone >= o.summary.auditsPlanned ? 'ok' : 'acc',
      })}
    </div>

    <div class="card">
      <div class="card-h">
        <div>
          <h2>Occurrences</h2>
          <p class="tiny dim">The reporting clock runs from detection — ${o.reportWindowHours} hours</p>
        </div>
      </div>
      <div class="scrollx">
        <table class="tbl">
          <thead>
            <tr>
              <th>Reference</th><th class="wide">Occurrence</th><th>Aircraft</th>
              <th class="col-opt">Occurred</th><th>Reporting</th><th></th>
            </tr>
          </thead>
          <tbody>
            ${o.occurrences.length
              ? o.occurrences
                  .map(
                    (x) => html`<tr>
                      <td class="st s-${x.overdue ? 'exceed' : x.reportedAt ? 'ok' : 'caution'} n">
                        ${esc(x.reference)}
                        <span class="sub2">${esc(CLASSIFICATION[x.classification ?? 'OTHER'] ?? '')}</span>
                      </td>
                      <td class="wide">
                        ${esc(x.title)}
                        <span class="sub2">${esc(x.narrative.slice(0, 110))}${x.narrative.length > 110 ? '…' : ''}</span>
                      </td>
                      <td class="n">
                        ${x.registration
                          ? `<a href="/fleet/${esc(x.aircraftId!)}" class="reg">${esc(x.registration)}</a>`
                          : '<span class="muted">—</span>'}
                      </td>
                      <td class="col-opt tiny muted">${esc(fmtDate(x.occurredAt))}</td>
                      <td>
                        ${pill(
                          x.overdue ? 'OVERDUE' : x.reportedAt ? 'OK' : 'DUE_SOON',
                          x.reportedAt ? 'Reported' : x.overdue ? 'Overdue' : 'Due',
                        )}
                        <span class="sub2">${esc(clock(x))}${x.authorityRef ? ` · ${esc(x.authorityRef)}` : ''}</span>
                      </td>
                      <td style="white-space:nowrap">
                        ${!x.reportedAt ? `<button class="btn sm" data-reported="${esc(x.id)}">Mark reported</button>` : ''}
                        ${x.status !== 'CLOSED' ? `<button class="btn sm" data-close="${esc(x.id)}">Close</button>` : ''}
                      </td>
                    </tr>`,
                  )
                  .join('')
              : '<tr><td colspan="6" class="muted">No occurrences recorded.</td></tr>'}
          </tbody>
        </table>
      </div>
      <div class="card-f">
        A reportable occurrence cannot be closed until it has been reported to the authority — closing it
        first would leave the obligation unmet and the record saying otherwise.
      </div>
    </div>

    <div class="cols">
      <div class="card">
        <div class="card-h"><h2>Findings</h2><span class="lbl">corrective action</span></div>
        <div class="rows">
          ${o.findings.length
            ? o.findings
                .map(
                  (f) => html`
                    <div class="row" style="border-left-color:var(--${
                      f.status === 'CLOSED' ? 'ok' : f.overdue || f.level === 'LEVEL_1' ? 'exceed' : 'caution'
                    })">
                      <div class="bd">
                        <div class="t">${esc(f.reference)} — ${esc(LEVEL_LABEL[f.level] ?? f.level)}</div>
                        <div class="m">
                          ${esc(f.description.slice(0, 150))}
                          ${f.regulatoryRef ? `<br />${esc(f.regulatoryRef)}` : ''}
                          ${f.correctiveDueAt ? ` · corrective action due ${esc(fmtDate(f.correctiveDueAt))}` : ''}
                        </div>
                      </div>
                      ${pill(
                        f.status === 'CLOSED' ? 'OK' : f.overdue ? 'OVERDUE' : 'DUE_SOON',
                        f.status === 'CLOSED' ? 'Closed' : f.overdue ? 'Overdue' : 'Open',
                      )}
                    </div>
                  `,
                )
                .join('')
            : '<div class="row"><div class="bd"><div class="m">No findings raised.</div></div></div>'}
        </div>
      </div>

      <div class="card">
        <div class="card-h"><h2>Audit plan</h2><span class="lbl">compliance monitoring</span></div>
        <div class="rows">
          ${o.audits.length
            ? o.audits
                .map(
                  (a) => html`
                    <div class="row">
                      <div class="bd">
                        <div class="t">${esc(a.reference)} — ${esc(a.title)}</div>
                        <div class="m">
                          ${esc(a.kind.toLowerCase())}${a.scope ? ` · ${esc(a.scope)}` : ''}
                          ${a.plannedFor ? ` · planned ${esc(fmtDate(a.plannedFor))}` : ''}
                        </div>
                      </div>
                      ${pill(a.status === 'DONE' ? 'OK' : 'NA', a.status === 'DONE' ? 'Done' : 'Planned')}
                    </div>
                  `,
                )
                .join('')
            : '<div class="row"><div class="bd"><div class="m">No audits planned.</div></div></div>'}
        </div>
      </div>
    </div>
  `;
}

// ── dialogs ─────────────────────────────────────────────────────────────────

function nowLocal(): string {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function reportOccurrence(o: SafetyOverview, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Report occurrence',
    sub: `Reg. (EU) 376/2014 — ${o.reportWindowHours} hours from becoming aware of it`,
    primary: 'Record occurrence',
    body: () => html`
      <div class="frow">
        ${field('Aircraft', select('aircraftId', [opt('', 'Not aircraft-specific'), ...o.fleet.map((a) => opt(a.id, a.registration))].join('')))}
        ${field('Classification', select('classification', [
          opt('INCIDENT', 'Incident'),
          opt('SERIOUS_INCIDENT', 'Serious incident'),
          opt('ACCIDENT', 'Accident'),
          opt('OTHER', 'Other occurrence'),
        ].join('')))}
      </div>
      <div class="frow">
        ${field('Occurred', input('occurredAt', nowLocal(), 'type="datetime-local"'))}
        ${field('Became aware', input('detectedAt', nowLocal(), 'type="datetime-local"'),
          `The ${o.reportWindowHours}-hour clock runs from here, not from when it happened`)}
      </div>
      ${field('Title', input('title', '', 'placeholder="Engine rough running on climb-out" maxlength="300"'))}
      ${field('What happened', input('narrative', '', 'maxlength="8000"'),
        'Enough for someone who was not there to understand it')}
      ${callout('caution', '!', html`
        Recording it here starts the reporting clock. It still has to be sent to the competent authority —
        mark it reported once it has been, with their reference.
      `)}
    `,
    collect: (root) => {
      const title = val(root, 'title');
      const narrative = val(root, 'narrative');
      if (!title) return { error: 'Give the occurrence a title.' };
      if (!narrative) return { error: 'Describe what happened — the narrative is the report.' };
      const occurredAt = val(root, 'occurredAt');
      const detectedAt = val(root, 'detectedAt');
      if (detectedAt && occurredAt && detectedAt < occurredAt) {
        return { error: 'An occurrence cannot be detected before it happened.' };
      }
      return {
        aircraftId: val(root, 'aircraftId') || undefined,
        classification: val(root, 'classification'),
        occurredAt: new Date(occurredAt).toISOString(),
        detectedAt: detectedAt ? new Date(detectedAt).toISOString() : undefined,
        title,
        narrative,
      };
    },
    submit: (payload) => api.safety.report(payload),
    done: (r) => `${r.reference} recorded — report by ${String(r.reportDueAt).slice(0, 16).replace('T', ' ')}`,
    onDone: reload,
  });
}

function markReported(occurrence: SafetyOccurrence, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Mark reported',
    sub: `${occurrence.reference} — ${occurrence.title}`,
    primary: 'Mark reported',
    body: () => html`
      ${field('Authority reference', input('authorityRef', '', 'placeholder="AACR/2026/0142" maxlength="80"'))}
      ${callout('info', 'i', html`
        Records that the report went to the competent authority and stops the ${'72'}-hour clock. The
        occurrence stays open until the follow-up action is recorded.
      `)}
    `,
    collect: (root) => ({ authorityRef: val(root, 'authorityRef') || undefined }),
    submit: (payload) => api.safety.markReported(occurrence.id, payload),
    done: (r) => `${r.reference} marked reported`,
    onDone: reload,
  });
}

function closeOccurrence(occurrence: SafetyOccurrence, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Close occurrence',
    sub: `${occurrence.reference} — ${occurrence.title}`,
    primary: 'Close',
    body: () => html`
      ${field('Follow-up action taken', input('followUpAction', '', 'maxlength="8000"'),
        'What was done so it does not happen again')}
      ${occurrence.isReportable && !occurrence.reportedAt
        ? callout('exceed', '✕', html`
            This occurrence is reportable and has not been reported yet. It cannot be closed until it has —
            closing it first would leave the obligation unmet and the record saying otherwise.
          `)
        : ''}
    `,
    collect: (root) => {
      const followUpAction = val(root, 'followUpAction');
      if (!followUpAction) return { error: 'Record the follow-up action before closing.' };
      return { followUpAction };
    },
    submit: (payload) => api.safety.close(occurrence.id, payload),
    done: (r) => `${r.reference} closed`,
    onDone: reload,
  });
}

function raiseFinding(o: SafetyOverview, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    wide: true,
    title: 'Raise finding',
    sub: 'From an audit, an occurrence, or day-to-day compliance monitoring',
    primary: 'Raise finding',
    body: () => html`
      <div class="frow">
        ${field('Level', select('level', [
          opt('LEVEL_2', 'Level 2 — corrective action required'),
          opt('LEVEL_1', 'Level 1 — significant, immediate action'),
          opt('OBSERVATION', 'Observation'),
        ].join('')))}
        ${field('Regulatory reference', input('regulatoryRef', '', 'placeholder="ML.A.302(c)" maxlength="120"'))}
      </div>
      ${field('Finding', input('description', '', 'maxlength="4000"'))}
      <div class="frow">
        ${field('Owner', select('ownerPersonId', [opt('', 'Unassigned'), ...o.people.map((p) => opt(p.id, p.displayName))].join('')))}
        ${field('Corrective action due', input('correctiveDueAt', '', 'type="date"'))}
      </div>
    `,
    collect: (root) => {
      const description = val(root, 'description');
      if (!description) return { error: 'Describe the finding.' };
      return {
        level: val(root, 'level'),
        description,
        regulatoryRef: val(root, 'regulatoryRef') || undefined,
        ownerPersonId: val(root, 'ownerPersonId') || undefined,
        correctiveDueAt: val(root, 'correctiveDueAt') || undefined,
      };
    },
    submit: (payload) => api.safety.raiseFinding(payload),
    done: (r) => `${r.reference} raised`,
    onDone: reload,
  });
}

export async function renderSafety(host: HTMLElement): Promise<void> {
  host.innerHTML = loading('Loading safety data');
  try {
    const o = await api.safety.overview();
    host.innerHTML = view(o);
    const reload = () => void renderSafety(host);

    host.querySelector('[data-report]')?.addEventListener('click', () => reportOccurrence(o, reload));
    host.querySelector('[data-finding]')?.addEventListener('click', () => raiseFinding(o, reload));
    host.querySelectorAll<HTMLElement>('[data-reported]').forEach((b) =>
      b.addEventListener('click', () => {
        const x = o.occurrences.find((y) => y.id === b.dataset.reported);
        if (x) markReported(x, reload);
      }),
    );
    host.querySelectorAll<HTMLElement>('[data-close]').forEach((b) =>
      b.addEventListener('click', () => {
        const x = o.occurrences.find((y) => y.id === b.dataset.close);
        if (x) closeOccurrence(x, reload);
      }),
    );
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'Could not load safety data.');
  }
}
